import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Snackbar, Stack, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import UndoIcon from '@mui/icons-material/UndoOutlined';
import RedoIcon from '@mui/icons-material/RedoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCutIcon from '@mui/icons-material/ContentCutOutlined';
import TitleIcon from '@mui/icons-material/TitleOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaptionOutlined';
import MusicNoteIcon from '@mui/icons-material/MusicNoteOutlined';
import CropIcon from '@mui/icons-material/CropOutlined';
import MovieIcon from '@mui/icons-material/MovieOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PaletteIcon from '@mui/icons-material/PaletteOutlined';
import KeyboardIcon from '@mui/icons-material/KeyboardOutlined';
import { useAuth } from '../../context/AuthContext';
import { fetchMulticamSet, fetchVideoById, type MulticamAngle } from '../../graphqlService';
import {
  EditingApiError,
  deleteEdit,
  duplicateEdit,
  fetchBrandKit,
  fetchEdit,
  fetchEditingAllowance,
  fetchAiStatus,
  fetchVoiceSample,
  restoreEditVersion,
  saveEdit,
  speakVoiceover,
  updateBrandKit,
  type BrandAsset,
  type BrandKit,
  type EditRecord,
  type EditingAllowance,
  type EditVersion,
  type ExportRecord,
  type SaveCheckpoint,
  type StudioTool,
} from '../../services/editingApi';
import { debugLog } from '../../utils/debugLog';
import type { EditList, OutputAspect } from '../../utils/editing/editList';
import { outputDurationMs, segmentPlacements } from '../../utils/editing/editList';
import { cuesFromWords, parseCaptionFile, placeFileCues, type TimedWord } from '../../utils/editing/captions';
import PageContainer from '../ui/PageContainer';
import { useVideoTranscript } from '../Transcript/useVideoTranscript';
import { brand } from '../../theme';
import BrandKitDialog from './BrandKitDialog';
import CommandBar from './CommandBar';
import ExportsPanel from './ExportsPanel';
import type { OverlaySelection, PreviewCue } from './PreviewOverlay';
import PreviewTransport from './PreviewTransport';
import ShortcutsDialog from './ShortcutsDialog';
import StageCanvas from './StageCanvas';
import Timeline, { type MarkedRange, type StudioSelection } from './Timeline';
import VersionsDialog from './VersionsDialog';
import {
  ASPECT_SHORT, addOverlay, addVoiceover, commit, defaultPosition, formatMs, frameMs as frameMsOf, historyOf, redo, removeItem, removeRange,
  removeSegment, replacePresent, resetToOriginal, segmentIdAt, splitAt, undo, type History, type SaveFirstResult,
} from './editStudioModel';
import CaptionsPanel from './panels/CaptionsPanel';
import CutPanel from './panels/CutPanel';
import OutputPanel from './panels/OutputPanel';
import PicturesPanel from './panels/PicturesPanel';
import SoundPanel from './panels/SoundPanel';
import TextPanel from './panels/TextPanel';
import { usePreviewAudio } from './usePreviewAudio';
import { usePreviewPlayer } from './usePreviewPlayer';
import { useFollowPerson } from './useFollowPerson';

const AUTOSAVE_MS = 1500;

type SaveOutcome = 'saved' | 'unchanged' | 'busy' | 'conflict' | 'error';
const CONFLICT_MESSAGE = 'This edit was changed in another tab or window, so what you see here isn’t what’s saved.';
/** Lets React finish the render a save's result started (the newest list, the saved list). */
const nextRender = () => new Promise<void>((resolve) => { window.setTimeout(resolve, 0); });
const STUDIO_FRAME_SX = { maxWidth: 1760, pt: { xs: 1.5, md: 2 }, pb: { xs: 6, md: 2 } } as const;
const SHAPES: OutputAspect[] = ['source', '16:9', '9:16', '1:1', '4:5'];

interface SourceMedia {
  videoUrl: string;
  name: string;
  /** The video whose transcript carries the captions. */
  transcriptVideoId: string | null;
  canTranscribe: boolean;
  multicam: boolean;
  /** A multicam set's angles, so the preview shows the angle each cut picks. */
  angles: MulticamAngle[];
  masterKey: string;
}

const TOOLS: Array<{ key: StudioTool; label: string; icon: React.ReactNode }> = [
  { key: 'cut', label: 'Cut', icon: <ContentCutIcon /> },
  { key: 'text', label: 'Text', icon: <TitleIcon /> },
  { key: 'pictures', label: 'Pictures', icon: <ImageIcon /> },
  { key: 'captions', label: 'Captions', icon: <ClosedCaptionIcon /> },
  { key: 'audio', label: 'Sound', icon: <MusicNoteIcon /> },
  { key: 'frame', label: 'Frame', icon: <CropIcon /> },
  { key: 'export', label: 'Export', icon: <MovieIcon /> },
];

const TOOL_KEYS = TOOLS.map((tool) => tool.key);

/** The first time each tab is opened in an edit with nothing of its kind yet, it says what it's for. */
type UploadIntent = 'logo' | 'picture' | 'music' | 'recording' | 'captions' | null;

const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  return Boolean(el.closest('input, textarea, select, [contenteditable="true"], [role="slider"] input'));
};

/** The quick-edit studio: a tool rail, the result on a stage in its own shape, an inspector, and a timeline. */
const EditStudio: React.FC = () => {
  const { editId } = useParams<{ editId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const compact = useMediaQuery('(max-width:899px)');
  // A short window (a laptop with the dock showing): the tool rail drops its labels to leave the room to the picture.
  const shortWindow = useMediaQuery('(max-height:800px)');
  const [edit, setEdit] = useState<EditRecord | null>(null);
  const [source, setSource] = useState<SourceMedia | null>(null);
  const [kit, setKit] = useState<BrandKit | null>(null);
  const [allowance, setAllowance] = useState<EditingAllowance | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [history, setHistory] = useState<History | null>(null);
  const [savedList, setSavedList] = useState<EditList | null>(null);
  const [saveState, setSaveState] = useState<'saved' | 'pending' | 'saving' | 'conflict' | 'error'>('saved');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const initialTool = new URLSearchParams(location.search).get('panel') as StudioTool | null;
  const [tool, setTool] = useState<StudioTool>(initialTool && TOOL_KEYS.indexOf(initialTool) >= 0 ? initialTool : 'cut');
  const [selection, setSelection] = useState<StudioSelection>(null);
  const [marked, setMarked] = useState<MarkedRange>({ fromMs: null, toMs: null });
  const [positionCrop, setPositionCrop] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const [uploadIntent, setUploadIntent] = useState<UploadIntent>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [muted, setMuted] = useState(false);
  const [toast, setToast] = useState<{ message: string; action?: { label: string; run: () => void } } | null>(null);
  const [voices, setVoices] = useState<string[]>([]);
  const [captionsOffered, setCaptionsOffered] = useState(false);
  const versionRef = useRef(0);
  const savingRef = useRef(false);
  // The newest values for saving, so a save that waits on another never works from an old render.
  const historyRef = useRef<History | null>(null);
  historyRef.current = history;
  const savedListRef = useRef<EditList | null>(null);
  savedListRef.current = savedList;
  const saveStateRef = useRef(saveState);
  saveStateRef.current = saveState;
  const saveMessageRef = useRef<string | null>(null);
  const saveInFlightRef = useRef<Promise<SaveOutcome> | null>(null);
  const checkpointRef = useRef<SaveCheckpoint | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  const timelineBoxRef = useRef<HTMLDivElement | null>(null);
  const inspectorRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const [stageMax, setStageMax] = useState(420);
  const list = history?.present ?? null;

  // --- Loading ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!editId) return;
      try {
        const [record, brandKit, plan] = await Promise.all([fetchEdit(editId), fetchBrandKit(), fetchEditingAllowance().catch(() => null)]);
        if (cancelled) return;
        if (!record) {
          setLoadError('This edit no longer exists.');
          return;
        }
        setEdit(record);
        setTitle(record.title);
        versionRef.current = record.version;
        setHistory(historyOf(record.list));
        setSavedList(record.list);
        setKit(brandKit);
        setAllowance(plan);
        const token = (await getToken()) || '';
        if (record.sourceKind === 'video' && record.videoId) {
          const video = await fetchVideoById(record.videoId, token);
          if (cancelled) return;
          setSource({ videoUrl: video.url, name: video.name, transcriptVideoId: record.videoId, canTranscribe: Boolean(plan?.planId === 'promax'), multicam: false, angles: [], masterKey: 'A' });
        } else if (record.multicamSetId) {
          const set = await fetchMulticamSet(record.multicamSetId);
          if (cancelled) return;
          const master = set?.angles.find((angle) => angle.angleKey === 'A') || set?.angles[0];
          const audioAngle = set?.audioSource?.videoId || set?.angles.find((angle) => angle.angleKey === set.audioAngleKey)?.videoId || master?.videoId || null;
          if (!set || !master) {
            setLoadError('This multicam set no longer exists.');
            return;
          }
          setSource({ videoUrl: master.videoUrl, name: set.name, transcriptVideoId: audioAngle, canTranscribe: Boolean(set.ownerFeatures?.transcripts), multicam: true, angles: set.angles, masterKey: master.angleKey });
        }
      } catch (error) {
        debugLog('Edit studio failed to load', error);
        if (!cancelled) setLoadError(error instanceof Error ? error.message : 'The edit couldn’t be loaded.');
      }
    })();
    return () => { cancelled = true; };
  }, [editId, getToken]);

  useEffect(() => {
    fetchAiStatus().then((status) => setVoices(status.available ? status.voices : [])).catch((error) => debugLog('AI status failed', error));
  }, []);

  const refreshAllowance = useCallback(() => {
    fetchEditingAllowance().then(setAllowance).catch((error) => debugLog('Allowance refresh failed', error));
  }, []);

  // --- Changing and saving ---------------------------------------------------
  const change = useCallback((next: EditList, key?: string) => {
    setHistory((current) => (current ? commit(current, next, key) : current));
  }, []);
  /** A change worked out from the newest list (for work that finishes later, like finding the person). */
  const changeWith = useCallback((update: (current: EditList) => EditList, key?: string) => {
    setHistory((current) => (current ? commit(current, update(current.present), key) : current));
  }, []);

  const dirty = Boolean(list && savedList && list !== savedList && JSON.stringify(list) !== JSON.stringify(savedList));

  const save = useCallback(async (titleToSave?: string, options: { force?: boolean } = {}): Promise<SaveOutcome> => {
    const h = historyRef.current;
    if (!edit || !h) return 'unchanged';
    if (savingRef.current) return 'busy';
    if (saveStateRef.current === 'conflict' && !options.force) return 'conflict';
    const current = h.present;
    const nextTitle = titleToSave ?? null;
    // "Keep mine" saves even an unchanged list: the other tab's version is the one on the server.
    if (!options.force && JSON.stringify(current) === JSON.stringify(savedListRef.current) && nextTitle === null) return 'unchanged';
    savingRef.current = true;
    saveStateRef.current = 'saving';
    setSaveState('saving');
    const checkpoint = checkpointRef.current;
    checkpointRef.current = null;
    const run = (async (): Promise<SaveOutcome> => {
      try {
        const saved = await saveEdit(edit.id, versionRef.current, current, nextTitle, checkpoint);
        versionRef.current = saved.version;
        savedListRef.current = saved.list;
        saveStateRef.current = 'saved';
        saveMessageRef.current = null;
        setEdit(saved);
        setSavedList(saved.list);
        // The server's canonical copy replaces ours only if nothing changed meanwhile.
        setHistory((latest) => (latest && latest.present === current ? replacePresent(latest, saved.list) : latest));
        setSaveState('saved');
        setSaveMessage(null);
        return 'saved';
      } catch (error) {
        if (checkpoint) checkpointRef.current = checkpoint;
        const apiError = error instanceof EditingApiError ? error : new EditingApiError(error);
        const outcome: SaveOutcome = apiError.code === 'EDIT_CONFLICT' ? 'conflict' : 'error';
        saveStateRef.current = outcome;
        saveMessageRef.current = apiError.message;
        setSaveState(outcome);
        setSaveMessage(apiError.message);
        return outcome;
      } finally {
        savingRef.current = false;
        saveInFlightRef.current = null;
      }
    })();
    saveInFlightRef.current = run;
    return run;
  }, [edit]);
  const saveRef = useRef(save);
  saveRef.current = save;

  /**
   * Saves now and waits for it (after any save already on its way), for an action that uses
   * the saved edit: an export, a send, a copy, a voice. It answers the version saved, or why not.
   */
  const saveNow = useCallback(async (): Promise<SaveFirstResult> => {
    const unsaved = () => {
      const h = historyRef.current;
      return Boolean(h && savedListRef.current && JSON.stringify(h.present) !== JSON.stringify(savedListRef.current));
    };
    for (let attempt = 0; attempt < 5; attempt += 1) {
      if (saveInFlightRef.current) {
        await saveInFlightRef.current;
        await nextRender();
        continue;
      }
      if (saveStateRef.current === 'conflict') return { ok: false, conflict: true, message: CONFLICT_MESSAGE };
      const outcome = await saveRef.current();
      if (outcome === 'conflict') return { ok: false, conflict: true, message: CONFLICT_MESSAGE };
      if (outcome === 'error') return { ok: false, conflict: false, message: `Your latest changes didn’t save${saveMessageRef.current ? `: ${saveMessageRef.current}` : '.'} Try again in a moment.` };
      if (outcome === 'busy') continue;
      await nextRender();
      if (!unsaved()) return { ok: true, version: versionRef.current };
    }
    return unsaved() ? { ok: false, conflict: false, message: 'Your changes are still saving. Try again in a moment.' } : { ok: true, version: versionRef.current };
  }, []);

  /** The server refused an action because another tab saved first: show the choice. */
  const markConflict = useCallback((message: string) => {
    saveStateRef.current = 'conflict';
    setSaveState('conflict');
    setSaveMessage(message);
  }, []);

  /** Another tab saved first: keep this tab's version, saved over the newer one. */
  const keepMine = useCallback(async () => {
    if (!edit) return;
    try {
      const latest = await fetchEdit(edit.id);
      if (latest) versionRef.current = latest.version;
      setSaveState('pending');
      setSaveMessage(null);
      await saveRef.current(undefined, { force: true });
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : 'That didn’t save.');
    }
  }, [edit]);

  /** Generates a spoken line's voice: the server saves the new version, and the studio takes it as its own. */
  const speak = useCallback(async (voiceoverId: string) => {
    if (!edit) return;
    const ready = await saveNow();
    if (!ready.ok) throw new Error(ready.conflict ? 'This edit was changed in another tab or window. Choose Keep mine or Load theirs at the top, then try again.' : ready.message);
    const saved = await speakVoiceover(edit.id, versionRef.current, voiceoverId);
    versionRef.current = saved.version;
    setEdit(saved);
    setSavedList(saved.list);
    setHistory((h) => (h ? replacePresent(h, saved.list) : h));
    fetchBrandKit().then(setKit).catch(() => undefined);
    setToast({ message: 'Voice generated. Press play beside the line to hear it.' });
  }, [edit, saveNow]);

  // Autosave a moment after the last change.
  useEffect(() => {
    if (!dirty || saveState === 'conflict') return undefined;
    setSaveState((state) => (state === 'saving' ? state : 'pending'));
    const timer = window.setTimeout(() => { save(); }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [dirty, list, save, saveState]);

  // Never lose a change: warn before leaving with one unsaved, and save at once when the tab is hidden.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty && saveState !== 'saving') return;
      event.preventDefault();
      event.returnValue = '';
    };
    const onHidden = () => { if (document.visibilityState === 'hidden' && dirty) saveRef.current(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, [dirty, saveState]);

  // --- Preview ---------------------------------------------------------------
  const player = usePreviewPlayer(list ?? EMPTY_LIST);
  const followPerson = useFollowPerson({ list, videoUrl: source?.videoUrl ?? null, angles: source?.multicam ? source.angles : undefined, masterKey: source?.masterKey, changeWith });
  const placements = useMemo(() => (list ? segmentPlacements(list) : []), [list]);
  const segmentMuted = Boolean(placements[player.segmentIndex]?.segment.muted);
  const levels = usePreviewAudio({ list: list ?? EMPTY_LIST, assets: kit?.assets || [], outputMs: player.outputMs, playing: player.playing, video: player.element, muted, segmentMuted, meter: tool === 'audio' });
  const transcript = useVideoTranscript(source?.transcriptVideoId || null, { withWords: true, canStart: Boolean(source?.canTranscribe) });
  const words = useMemo<TimedWord[]>(() => {
    const out: TimedWord[] = [];
    transcript.lines.forEach((line) => {
      if (line.words && line.words.length) line.words.forEach((word) => out.push({ text: word.text, start: word.start, end: word.end }));
      else out.push({ text: line.text, start: line.start, end: line.end });
    });
    return out;
  }, [transcript.lines]);
  const [fileCues, setFileCues] = useState<{ assetId: string; cues: ReturnType<typeof parseCaptionFile> } | null>(null);
  useEffect(() => {
    const assetId = list?.captions?.source === 'asset' ? list.captions.assetId : null;
    const asset = assetId ? kit?.assets.find((candidate) => candidate.id === assetId) : null;
    if (!asset || fileCues?.assetId === asset.id) return undefined;
    let cancelled = false;
    fetch(asset.url).then((response) => response.text()).then((text) => {
      if (!cancelled) setFileCues({ assetId: asset.id, cues: parseCaptionFile(text) });
    }).catch((error) => debugLog('Caption file could not be read', error));
    return () => { cancelled = true; };
  }, [fileCues, kit, list]);
  const cues = useMemo<PreviewCue[]>(() => {
    if (!list || !list.captions) return [];
    if (list.captions.source === 'asset') return fileCues && fileCues.assetId === list.captions.assetId ? placeFileCues(fileCues.cues, list) : [];
    return cuesFromWords(words, list, list.captions.style);
  }, [fileCues, list, words]);

  // A Short with a transcript and no captions: offer them once.
  useEffect(() => {
    if (!list || captionsOffered || list.captions || list.output.aspect !== '9:16' || transcript.phase !== 'done' || words.length === 0) return;
    setCaptionsOffered(true);
    setToast({
      message: 'Shorts are mostly watched without sound. Add captions?',
      action: { label: 'Add pop captions', run: () => { setTool('captions'); change({ ...list, captions: { source: 'transcript', assetId: null, mode: 'burn', language: null, style: { font: 'sans', sizePct: 7, color: '#FFFFFF', backgroundColor: null, placement: 'middle', maxCharsPerLine: 42, maxLines: 2, look: 'pop', highlightColor: '#FFD84D' } } }); } },
    });
  }, [captionsOffered, change, list, transcript.phase, words.length]);

  const canUndo = Boolean(history && history.past.length > 0);
  const canRedo = Boolean(history && history.future.length > 0);
  const fps = list?.output.fps ?? 30;
  const oneFrame = frameMsOf(fps);
  const canPositionCrop = Boolean(list && list.output.aspect !== 'source' && list.reframe?.mode !== 'pad');

  // Selecting on the timeline or the picture opens the right tool.
  const select = useCallback((next: StudioSelection) => {
    setSelection(next);
    if (!next) return;
    if (next.kind === 'segment') setTool('cut');
    else if (next.kind === 'text') setTool('text');
    else if (next.kind === 'overlay') setTool('pictures');
    else if (next.kind === 'captions') setTool('captions');
    else setTool('audio');
  }, []);
  const overlaySelection: OverlaySelection = selection && (selection.kind === 'text' || selection.kind === 'overlay') ? { kind: selection.kind, id: selection.id } : null;

  const deleteSelected = useCallback(() => {
    if (!list || !selection) return false;
    if (selection.kind === 'segment') { change(removeSegment(list, selection.id)); setSelection(null); return true; }
    if (selection.kind === 'text') { change({ ...list, texts: removeItem(list.texts, selection.id) }); setSelection(null); return true; }
    if (selection.kind === 'overlay') { change({ ...list, overlays: removeItem(list.overlays, selection.id) }); setSelection(null); return true; }
    if (selection.kind === 'voiceover') { change({ ...list, voiceovers: removeItem(list.voiceovers, selection.id) }); setSelection(null); return true; }
    return false;
  }, [change, list, selection]);

  const fullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    else if (el.requestFullscreen) el.requestFullscreen().catch(() => undefined);
  }, []);

  // --- Fitting the window ------------------------------------------------------
  // On a desktop the stage takes the height left once the top bar, the assistant
  // and the timeline have theirs, so the picture and the timeline are on screen
  // together (a short laptop window scrolls only below a usable minimum).
  const ready = Boolean(list && source && edit);
  useLayoutEffect(() => {
    if (!ready) return undefined;
    const measure = () => {
      const column = stageRef.current;
      const canvasBox = column ? column.querySelector<HTMLElement>('[data-testid="stage"]') : null;
      if (!column || !canvasBox) return;
      // What the stage column holds besides the canvas (its padding, the transport, a note), from
      // the parts themselves: the column may be stretched taller by the rail beside it.
      const style = window.getComputedStyle(column);
      let extras = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      Array.from(column.children).forEach((child) => {
        if (child === canvasBox) return;
        const childStyle = window.getComputedStyle(child);
        extras += (child as HTMLElement).offsetHeight + (parseFloat(childStyle.marginTop) || 0) + (parseFloat(childStyle.marginBottom) || 0);
      });
      let next: number;
      if (typeof document !== 'undefined' && document.fullscreenElement === column) next = Math.max(200, window.innerHeight - extras);
      else if (compact) next = Math.min(420, Math.max(220, window.innerHeight * 0.5));
      else {
        const top = column.getBoundingClientRect().top + window.scrollY;
        const below = (timelineBoxRef.current ? timelineBoxRef.current.offsetHeight : 0) + 16;
        // Never shorter than the tool rail beside it (the row is at least that tall anyway).
        const railNeed = railRef.current ? railRef.current.offsetHeight - extras : 0;
        next = Math.max(railNeed, Math.min(720, Math.max(200, window.innerHeight - top - extras - below)));
      }
      next = Math.round(next);
      setStageMax((current) => (Math.abs(current - next) > 1 ? next : current));
    };
    measure();
    window.addEventListener('resize', measure);
    document.addEventListener('fullscreenchange', measure);
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => measure());
      if (topRef.current) observer.observe(topRef.current);
      if (timelineBoxRef.current) observer.observe(timelineBoxRef.current);
    }
    return () => {
      window.removeEventListener('resize', measure);
      document.removeEventListener('fullscreenchange', measure);
      if (observer) observer.disconnect();
    };
  }, [ready, compact, shortWindow]);

  // Each panel opens at its top, not where the last one was scrolled to.
  useEffect(() => {
    const el = inspectorRef.current;
    if (el) el.scrollTop = 0;
  }, [tool]);

  // --- Keyboard --------------------------------------------------------------
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target) || !list) return;
      const meta = event.metaKey || event.ctrlKey;
      const key = event.key;
      if (meta && key.toLowerCase() === 'z') {
        event.preventDefault();
        setHistory((h) => (h ? (event.shiftKey ? redo(h) : undo(h)) : h));
        return;
      }
      if (meta || event.altKey) return;
      const hasMark = marked.fromMs !== null && marked.toMs !== null && marked.toMs > marked.fromMs;
      switch (key) {
        case ' ':
          event.preventDefault();
          player.toggle();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (player.playing) player.pause();
          player.seek(player.outputMs - (event.shiftKey ? 5000 : oneFrame));
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (player.playing) player.pause();
          player.seek(player.outputMs + (event.shiftKey ? 5000 : oneFrame));
          break;
        case 'Home':
          event.preventDefault();
          player.seek(0);
          break;
        case 'End':
          event.preventDefault();
          player.seek(player.durationMs);
          break;
        case 'i':
        case 'I':
          setMarked((m) => ({ fromMs: Math.round(player.outputMs), toMs: m.toMs !== null && m.toMs > player.outputMs ? m.toMs : null }));
          setTool('cut');
          break;
        case 'o':
        case 'O':
          setMarked((m) => ({ fromMs: m.fromMs !== null && m.fromMs < player.outputMs ? m.fromMs : 0, toMs: Math.round(player.outputMs) }));
          setTool('cut');
          break;
        case 's':
        case 'S': {
          const next = splitAt(list, player.outputMs);
          if (next !== list) {
            change(next);
            const id = segmentIdAt(next, player.outputMs + 1);
            if (id) select({ kind: 'segment', id });
          }
          break;
        }
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          if (hasMark) {
            change(removeRange(list, marked.fromMs as number, marked.toMs as number));
            player.seek(marked.fromMs as number);
            setMarked({ fromMs: null, toMs: null });
          } else {
            deleteSelected();
          }
          break;
        case 'l':
        case 'L':
          if (player.loop) player.setLoop(null);
          else if (hasMark) { player.setLoop({ fromMs: marked.fromMs as number, toMs: marked.toMs as number }); player.play(); }
          break;
        case 'm':
        case 'M':
          setMuted((m) => !m);
          break;
        case 'f':
        case 'F':
          fullscreen();
          break;
        case 'Escape':
          setMarked({ fromMs: null, toMs: null });
          setSelection(null);
          setPositionCrop(false);
          break;
        case '?':
          setShortcutsOpen(true);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [change, deleteSelected, fullscreen, list, marked, oneFrame, player, select]);

  // --- Brand kit: what's uploaded from a tool goes into the edit at once -------------------
  const openUpload = (intent: UploadIntent) => {
    setUploadIntent(intent);
    setKitOpen(true);
  };
  const onUploaded = async (assets: BrandAsset[], updatedKit: BrandKit) => {
    if (!list || !uploadIntent) return;
    const first = assets[0];
    if (!first) return;
    let next = list;
    if ((uploadIntent === 'logo' || uploadIntent === 'picture') && first.kind === 'image') {
      if (uploadIntent === 'logo' || !updatedKit.logoAssetId) {
        try { setKit(await updateBrandKit({ logoAssetId: first.id })); } catch (error) { debugLog('Logo not set', error); }
      }
      next = addOverlay(next, first.id, defaultPosition(uploadIntent === 'logo' ? 'top-right' : 'center', uploadIntent === 'logo' ? 3 : 0));
      const added = next.overlays[next.overlays.length - 1];
      change(next);
      if (added) select({ kind: 'overlay', id: added.id });
      setToast({ message: uploadIntent === 'logo' ? 'Your logo is in the brand kit and on the video. Drag it to move it.' : 'The picture is on the video. Drag it to place it.' });
    } else if (uploadIntent === 'music' && first.kind === 'audio') {
      change({ ...next, music: { assetId: first.id, startMs: 0, inMs: 0, gainDb: -14, ducking: true, duckDb: 12, fadeInMs: 1000, fadeOutMs: 2000, loop: true } });
      setToast({ message: 'The music is under your video, lowered while people speak.' });
    } else if (uploadIntent === 'recording' && first.kind === 'audio') {
      change(addVoiceover(next, first.id, player.outputMs));
      setToast({ message: 'The recording is placed at the playhead.' });
    }
    setUploadIntent(null);
    setKitOpen(false);
  };

  // --- The ⋯ menu ----------------------------------------------------------------------------
  const duplicate = async () => {
    setMenuAnchor(null);
    if (!edit) return;
    try {
      const ready = await saveNow();
      if (!ready.ok) throw new Error(ready.conflict ? 'This edit was changed in another tab or window. Choose Keep mine or Load theirs at the top, then make the copy.' : ready.message);
      const copy = await duplicateEdit(edit.id);
      setToast({ message: `Made “${copy.title}”.`, action: { label: 'Open it', run: () => navigate(`/edit/${copy.id}`) } });
    } catch (error) {
      setToast({ message: error instanceof Error ? error.message : 'It couldn’t be copied.' });
    }
  };
  const startOver = () => {
    setMenuAnchor(null);
    if (!list) return;
    if (!window.confirm('Start over from the untouched video? Every cut, text, picture, caption and sound comes off. Undo, or Earlier versions, brings them back.')) return;
    checkpointRef.current = { reason: 'reset', label: null };
    change(resetToOriginal(list));
    setSelection(null);
    setMarked({ fromMs: null, toMs: null });
  };
  const remove = async () => {
    setMenuAnchor(null);
    if (!edit) return;
    if (!window.confirm(`Delete “${edit.title}”? Its exports stay until they expire; the edit itself can’t be brought back.`)) return;
    try {
      await deleteEdit(edit.id);
      navigate('/library?tab=edits');
    } catch (error) {
      setToast({ message: error instanceof Error ? error.message : 'It couldn’t be deleted.' });
    }
  };
  const restore = async (version: EditVersion) => {
    if (!edit) return;
    const ready = await saveNow();
    if (!ready.ok) throw new Error(ready.conflict ? 'This edit was changed in another tab or window. Choose Keep mine or Load theirs at the top first.' : ready.message);
    const saved = await restoreEditVersion(edit.id, version.id, versionRef.current);
    versionRef.current = saved.version;
    setEdit(saved);
    setSavedList(saved.list);
    setHistory((h) => (h ? commit(h, saved.list) : historyOf(saved.list)));
    setToast({ message: 'Restored. The version you had is kept too, under Earlier versions.' });
  };

  const onExportFinished = useCallback((record: ExportRecord) => {
    setToast({ message: 'Your export is ready.', action: { label: 'Watch it', run: () => setTool('export') } });
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.visibilityState === 'hidden') {
      try { new Notification('Your PithPlay export is ready', { body: record.title }); } catch { /* not allowed here */ }
    }
  }, []);

  if (loadError) {
    return (
      <PageContainer sx={STUDIO_FRAME_SX}>
        <Alert severity="error" action={<Button onClick={() => navigate('/library')}>Library</Button>}>{loadError}</Alert>
      </PageContainer>
    );
  }
  if (!edit || !list || !source) {
    return (
      <PageContainer sx={STUDIO_FRAME_SX}>
        <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /><Typography variant="body2" sx={{ mt: 2 }}>Opening your edit…</Typography></Stack>
      </PageContainer>
    );
  }

  const saveLabel = saveState === 'saved' ? 'Saved' : saveState === 'pending' ? 'Unsaved changes' : saveState === 'saving' ? 'Saving…' : saveState === 'conflict' ? 'Changed elsewhere' : 'Not saved';
  const transcriptForPanels = { phase: transcript.phase, wordCount: words.length, canStart: Boolean(source.canTranscribe), start: () => { transcript.start(); }, problem: transcript.problem };
  const selectedSegmentId = selection?.kind === 'segment' ? selection.id : null;

  const inspector = (
    <Box sx={{ p: 2 }} data-testid={`inspector-${tool}`}>
      {tool === 'cut' ? (
        <CutPanel
          list={list}
          outputMs={player.outputMs}
          marked={marked}
          onMarked={setMarked}
          selectedSegmentId={selectedSegmentId}
          onSelectSegment={(id) => (id ? select({ kind: 'segment', id }) : setSelection(null))}
          onChange={change}
          onSeek={player.seek}
          frameMs={oneFrame}
          words={words}
          transcript={{ ready: transcript.phase === 'done', canStart: Boolean(source.canTranscribe) && transcript.phase === 'none', start: () => { transcript.start(); }, working: transcript.phase === 'working' || transcript.phase === 'loading' }}
        />
      ) : null}
      {tool === 'text' ? <TextPanel list={list} kit={kit} outputMs={player.outputMs} selectedId={selection?.kind === 'text' ? selection.id : null} onSelect={(id) => setSelection(id ? { kind: 'text', id } : null)} onChange={change} onSeek={player.seek} /> : null}
      {tool === 'pictures' ? <PicturesPanel list={list} kit={kit} selectedId={selection?.kind === 'overlay' ? selection.id : null} onSelect={(id) => setSelection(id ? { kind: 'overlay', id } : null)} onChange={change} onUpload={() => openUpload(kit?.logoAssetId ? 'picture' : 'logo')} onOpenBrandKit={() => { setUploadIntent(null); setKitOpen(true); }} /> : null}
      {tool === 'captions' ? <CaptionsPanel list={list} kit={kit} onChange={change} transcript={transcriptForPanels} transcriptVideoId={source.transcriptVideoId} onOpenBrandKit={() => openUpload('captions')} /> : null}
      {tool === 'audio' ? (
        <SoundPanel
          list={list}
          kit={kit}
          outputMs={player.outputMs}
          onChange={change}
          onOpenBrandKit={() => openUpload(list.music ? 'recording' : 'music')}
          voices={voices}
          onSpeak={speak}
          onSample={async (voice) => (await fetchVoiceSample(voice)).url}
          levels={player.playing ? levels : null}
        />
      ) : null}
      {tool === 'frame' ? <OutputPanel list={list} allowance={allowance} onChange={change} positionCrop={positionCrop} onTogglePositionCrop={() => setPositionCrop((v) => !v)} follow={followPerson} atMs={player.sourceMs} /> : null}
      {tool === 'export' ? (
        <ExportsPanel
          editId={edit.id}
          editTitle={edit.title}
          source={{ videoId: edit.videoId, multicamSetId: edit.multicamSetId }}
          editVersion={edit.version}
          dirty={saveState !== 'conflict' && (dirty || saveState === 'pending' || saveState === 'saving')}
          prepare={saveNow}
          conflict={saveState === 'conflict' ? { onKeepMine: keepMine, onLoadTheirs: () => window.location.reload() } : null}
          onConflict={markConflict}
          outputDurationMs={outputDurationMs(list)}
          resolution={list.output.resolution}
          aspect={list.output.aspect}
          allowance={allowance}
          onAllowanceChanged={refreshAllowance}
          onFinished={onExportFinished}
        />
      ) : null}
    </Box>
  );

  const iconOnly = shortWindow && !compact;
  const railItems = TOOLS.map((item) => {
    const active = tool === item.key;
    const button = (
      <Box
        key={item.key}
        component="button"
        type="button"
        onClick={() => setTool(item.key)}
        aria-current={active ? 'page' : undefined}
        aria-label={iconOnly ? item.label : undefined}
        data-testid={`tool-${item.key}`}
        sx={{
          border: 0, bgcolor: active ? 'rgba(37,99,235,0.08)' : 'transparent', color: active ? 'primary.main' : 'text.secondary', cursor: 'pointer',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25, py: iconOnly ? 0.75 : 1, px: compact ? 1.5 : 0.5, minWidth: compact ? 72 : 'auto', minHeight: iconOnly ? 40 : 48,
          borderLeft: compact ? 0 : `3px solid ${active ? '#2563EB' : 'transparent'}`, borderBottom: compact ? `3px solid ${active ? '#2563EB' : 'transparent'}` : 0,
          font: 'inherit', fontSize: 11, fontWeight: active ? 700 : 500,
        }}
      >
        {item.icon}
        {iconOnly ? null : item.label}
      </Box>
    );
    return iconOnly ? <Tooltip key={item.key} title={item.label} placement="right">{button}</Tooltip> : button;
  });
  const rail = compact ? (
    <Box component="nav" aria-label="Tools" sx={{ display: 'flex', overflowX: 'auto', borderTop: `1px solid ${brand.border}`, borderBottom: `1px solid ${brand.border}`, bgcolor: 'background.paper', position: 'sticky', top: 0, zIndex: 2 }}>
      {railItems}
    </Box>
  ) : (
    // The cell carries the border down the row; the rail inside keeps its own height, which the stage measures.
    <Box sx={{ borderRight: `1px solid ${brand.border}` }}>
      <Box component="nav" aria-label="Tools" ref={railRef} sx={{ display: 'flex', flexDirection: 'column', py: iconOnly ? 0.5 : 1 }}>
        {railItems}
      </Box>
    </Box>
  );

  return (
    <PageContainer sx={STUDIO_FRAME_SX}>
      <Box ref={topRef}>
      {/* Top bar: where you are, saving, undo, the shape, and Export */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <Tooltip title="Back to the library"><IconButton onClick={() => navigate('/library?tab=edits')} aria-label="Back to the library"><ArrowBackIcon /></IconButton></Tooltip>
        <TextField
          variant="standard"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => { if (title.trim() && title.trim() !== edit.title) save(title.trim()); else setTitle(edit.title); }}
          inputProps={{ 'aria-label': 'Edit title', maxLength: 120, 'data-testid': 'edit-title', style: { fontSize: 18, fontWeight: 600 } }}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <Chip size="small" label={saveLabel} color={saveState === 'conflict' || saveState === 'error' ? 'error' : saveState === 'saved' ? 'success' : 'default'} variant="outlined" data-testid="save-state" />
        <Tooltip title="Undo (⌘Z)"><span><IconButton onClick={() => setHistory((h) => (h ? undo(h) : h))} disabled={!canUndo} aria-label="Undo"><UndoIcon /></IconButton></span></Tooltip>
        <Tooltip title="Redo (⇧⌘Z)"><span><IconButton onClick={() => setHistory((h) => (h ? redo(h) : h))} disabled={!canRedo} aria-label="Redo"><RedoIcon /></IconButton></span></Tooltip>
        <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', sm: 'flex' } }} role="radiogroup" aria-label="Shape" data-testid="shape-chips">
          {SHAPES.map((shape) => (
            <Chip
              key={shape}
              size="small"
              role="radio"
              aria-checked={list.output.aspect === shape}
              label={ASPECT_SHORT[shape]}
              color={list.output.aspect === shape ? 'primary' : 'default'}
              variant={list.output.aspect === shape ? 'filled' : 'outlined'}
              onClick={() => change({ ...list, output: { ...list.output, aspect: shape }, reframe: shape === 'source' ? null : list.reframe || { focusXPct: 50, focusYPct: 45, mode: 'crop', track: [] } })}
            />
          ))}
        </Stack>
        <Tooltip title="More"><IconButton onClick={(event) => setMenuAnchor(event.currentTarget)} aria-label="More"><MoreVertIcon /></IconButton></Tooltip>
        <Button variant="contained" onClick={() => setTool('export')} data-testid="go-export">Export</Button>
      </Stack>
      <Menu open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); setVersionsOpen(true); }}><ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon><ListItemText>Earlier versions</ListItemText></MenuItem>
        <MenuItem onClick={duplicate}><ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon><ListItemText>Make a copy</ListItemText></MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setUploadIntent(null); setKitOpen(true); }}><ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon><ListItemText>Brand kit</ListItemText></MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setShortcutsOpen(true); }}><ListItemIcon><KeyboardIcon fontSize="small" /></ListItemIcon><ListItemText>Keyboard shortcuts</ListItemText></MenuItem>
        <Divider />
        <MenuItem onClick={startOver}><ListItemIcon><RestartAltIcon fontSize="small" /></ListItemIcon><ListItemText>Start over</ListItemText></MenuItem>
        <MenuItem onClick={remove} sx={{ color: 'error.main' }}><ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Delete this edit</ListItemText></MenuItem>
      </Menu>
      {saveState === 'conflict' || saveState === 'error' ? (
        <Alert
          severity={saveState === 'conflict' ? 'warning' : 'error'}
          sx={{ mb: 1.5 }}
          action={saveState === 'conflict' ? (
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={keepMine} data-testid="keep-mine">Keep mine</Button>
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>Load theirs</Button>
            </Stack>
          ) : <Button color="inherit" size="small" onClick={() => { setSaveState('pending'); save(); }} data-testid="retry-save">Retry</Button>}
        >
          {saveState === 'conflict' ? 'This edit was changed in another tab or window. Keep this one (it saves over the other), or load the other one.' : saveMessage}
        </Alert>
      ) : null}
      <CommandBar
        editId={edit.id}
        list={list}
        onApply={(next) => { checkpointRef.current = { reason: 'assistant', label: null }; change(next); }}
        onUndo={() => setHistory((h) => (h ? undo(h) : h))}
        onOpenPanel={setTool}
        transcript={{ canStart: Boolean(source.canTranscribe) && transcript.phase === 'none', start: () => { transcript.start(); } }}
      />
      </Box>
      <Box sx={{ border: `1px solid ${brand.border}`, borderRadius: 3, bgcolor: 'background.paper', overflow: 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : `${iconOnly ? 56 : 72}px minmax(0, 1fr) 380px`, alignItems: 'stretch' }}>
          {compact ? null : rail}
          <Box sx={{ bgcolor: '#0B0F19', p: shortWindow ? 1 : { xs: 1, md: 1.5 }, minWidth: 0 }} ref={stageRef}>
            {source.multicam ? <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mb: 0.5 }}>The preview follows your camera cuts; the export uses the set’s own sound.</Typography> : null}
            <StageCanvas
              list={list}
              player={player}
              videoUrl={source.videoUrl}
              assets={kit?.assets || []}
              cues={cues}
              positionCrop={positionCrop && canPositionCrop}
              maxHeight={stageMax}
              selected={overlaySelection}
              onSelect={(next) => (next ? select(next) : setSelection((current) => (current && (current.kind === 'text' || current.kind === 'overlay') ? null : current)))}
              onChange={change}
              angles={source.multicam ? source.angles : undefined}
              masterKey={source.masterKey}
            />
            <PreviewTransport
              player={player}
              muted={muted}
              onToggleMute={() => setMuted((m) => !m)}
              frameMs={oneFrame}
              marked={marked}
              canPositionCrop={canPositionCrop}
              positionCrop={positionCrop}
              onTogglePositionCrop={() => setPositionCrop((v) => !v)}
              onFullscreen={fullscreen}
              onShowShortcuts={() => setShortcutsOpen(true)}
            />
          </Box>
          {compact ? rail : null}
          {compact ? (
            <Box sx={{ minWidth: 0 }}>{inspector}</Box>
          ) : (
            // The stage sets the row's height; the inspector scrolls inside it.
            <Box sx={{ borderLeft: `1px solid ${brand.border}`, position: 'relative', minWidth: 0 }}>
              <Box ref={inspectorRef} sx={{ position: 'absolute', inset: 0, overflowY: 'auto' }} data-testid="inspector-scroll">{inspector}</Box>
            </Box>
          )}
        </Box>
        <Box ref={timelineBoxRef} sx={{ borderTop: `1px solid ${brand.border}`, bgcolor: '#0B1220' }}>
          <Timeline
            list={list}
            outputMs={player.outputMs}
            durationMs={player.durationMs}
            playing={player.playing}
            videoUrl={source.multicam ? null : source.videoUrl}
            assets={kit?.assets || []}
            words={words}
            cues={cues}
            selection={selection}
            marked={marked}
            onSeek={player.seek}
            onSelect={select}
            onMarked={setMarked}
            onChange={change}
          />
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {source.name} · result {formatMs(outputDurationMs(list))} · {edit.summary}
      </Typography>
      <BrandKitDialog
        open={kitOpen}
        kit={kit}
        uploadKind={uploadIntent === 'logo' || uploadIntent === 'picture' ? 'image' : uploadIntent === 'music' || uploadIntent === 'recording' ? 'audio' : uploadIntent === 'captions' ? 'captions' : null}
        onClose={() => { setKitOpen(false); setUploadIntent(null); }}
        onKit={(updated) => setKit(updated)}
        onUploaded={onUploaded}
      />
      <VersionsDialog open={versionsOpen} editId={edit.id} onClose={() => setVersionsOpen(false)} onRestore={restore} />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.action ? 8000 : 3500}
        onClose={() => setToast(null)}
        message={toast?.message}
        action={toast?.action ? <Button color="secondary" size="small" onClick={() => { toast.action?.run(); setToast(null); }}>{toast.action.label}</Button> : undefined}
      />
    </PageContainer>
  );
};

const EMPTY_LIST: EditList = {
  version: 1,
  source: { kind: 'video', videoId: '' },
  sourceDurationMs: 1,
  output: { resolution: '1080p', aspect: 'source', fps: null },
  segments: [],
  multicamCut: [],
  overlays: [],
  texts: [],
  captions: null,
  music: null,
  voiceovers: [],
  audio: { originalGainDb: 0, normalize: true },
  reframe: null,
};

export default EditStudio;
